import React from 'react';

import { MEDICAL_STAFF_TYPES_OF_WORK } from '@pflegia/types/api/candidate/type-of-work';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import AvailableJobs from '@pflegia/ui/shared/available-jobs';
import { getButtonClassName } from '@pflegia/ui/shared/button';
import ButtonLink from '@pflegia/ui/shared/button-link/ButtonLink';
import ContainerSection from '@pflegia/ui/shared/container-section';
import GuestHeroImage from '@pflegia/ui/shared/guest-hero-image';
import QuestionsAndAnswers from '@pflegia/ui/shared/landing-page/questions-and-answers/QuestionsAndAnswers';
import { TestimonialSection } from '@pflegia/ui/shared/testimonial';

import PraxiaHeader from '@public/assets/praxia-header.png';

import { FUNNEL } from '@shared/application/constants';
import { RegisterButtonTrackingIds } from '@shared/application/marketing/events/register-button';

import Advantages from './components/advantages';
import Employers from './components/employers/Employers';
import GuestHeader from './components/header/GuestHeader';
import Headline from './components/headline/Headline';
import PflegiaHowItWorksCta from './components/how-it-works/PflegiaHowItWorksCTA';
import OfferedValues from './components/offered-values';
import PflegiaFriends from './components/pflegia-friends';
import PraxiaJobTitles from './components/praxia-jobtitles';
import SearchFlow from './components/search-flow';
import TopBar from './components/top-bar/TopBar';
import TypeOfWorkButtons from './components/type-of-work-buttons/TypeOfWorkButtons';
import { ADVANTAGES } from './constants/guest-landing-page';
import { EMPLOYER_LOGOS, OFFERED_VALUES, PRACTICES, PRAXIA_FAQ, SEARCH_FLOW_STEPS, TESTIMONIALS } from './constants/praxia-landing-page';
import useLandingPageSharedFunctions from './hooks/use-landing-page-shared-functions';

const PraxiaLandingPage: React.FC = () => {
  const { t, getTestimonialPropsFromName, signupButtonClickTrackEventRegistration, headerRef, headerInView } =
    useLandingPageSharedFunctions();
  const { sourceDomainName } = useFlavorFromRoute();

  return (
    <>
      <TopBar />
      <main className='mb-20 flex flex-col gap-24 md:gap-40'>
        <div className='bg-primary-100'>
          <ContainerSection className='2xl:max-w-8xl'>
            <div ref={headerRef}>
              <GuestHeader
                headerImage={<GuestHeroImage src={PraxiaHeader} />}
                titleKey='praxiaTitle'
                heroButtons={<TypeOfWorkButtons typesOfWork={MEDICAL_STAFF_TYPES_OF_WORK} />}
              />
            </div>
          </ContainerSection>
        </div>
        <ContainerSection>
          <Advantages advantages={ADVANTAGES} maxCols={4} titleTranslationKey='whyPraxiaGuest' />
        </ContainerSection>
        <TestimonialSection {...getTestimonialPropsFromName(TESTIMONIALS[0])} />
        <ContainerSection>
          <AvailableJobs
            items={PRACTICES}
            headline={<Headline i18nKey='landingPage:availableJobs.praxiaTitle' subTitle='landingPage:availableJobs.praxiaSubtitle' />}
          />
        </ContainerSection>
        <TestimonialSection {...getTestimonialPropsFromName(TESTIMONIALS[1])} imgOnRight />
        <PraxiaJobTitles />
        <ContainerSection>
          <PflegiaHowItWorksCta registerButtonId={RegisterButtonTrackingIds.LandingPageHowItWorks} />
        </ContainerSection>
        <TestimonialSection {...getTestimonialPropsFromName(TESTIMONIALS[2])} />
        <ContainerSection>
          <OfferedValues values={OFFERED_VALUES} />
        </ContainerSection>
        <ContainerSection>
          <SearchFlow steps={SEARCH_FLOW_STEPS} />
        </ContainerSection>
        {/* eslint-disable-next-line no-magic-numbers */}
        <TestimonialSection {...getTestimonialPropsFromName(TESTIMONIALS[3])} imgOnRight />
        <ContainerSection>
          <Employers employerLogos={EMPLOYER_LOGOS} employerCount={1000} />
        </ContainerSection>
        <ContainerSection className='max-w-5xl'>
          <QuestionsAndAnswers faqs={PRAXIA_FAQ} sourceDomainName={sourceDomainName} />
        </ContainerSection>
        {!headerInView && (
          <ButtonLink
            href={FUNNEL}
            className={getButtonClassName('primary', 'fixed right-3 bottom-3 z-20 animate-[fade-in_300ms_forwards] cursor-pointer')}
            onClick={() => signupButtonClickTrackEventRegistration(RegisterButtonTrackingIds.LandingPageRightBottomCornerId)}>
            {t('findAJob')}
          </ButtonLink>
        )}
      </main>
      <PflegiaFriends />
    </>
  );
};

export default PraxiaLandingPage;
